import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';

const STATUS_LABELS = {
  new: 'New Application',
  under_review: 'Under Review',
  accepted: 'Approved',
  declined: 'Declined',
  awaiting_payment: 'Awaiting Payment',
  paid: 'Paid',
  awaiting_scheduling: 'Paid · Awaiting Booking',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const STATUS_COLORS = {
  new: 'text-emerald-700',
  under_review: 'text-neutral-700',
  accepted: 'text-[#C8A15A]',
  declined: 'text-red-600',
  awaiting_payment: 'text-amber-700',
  paid: 'text-emerald-800',
  awaiting_scheduling: 'text-blue-700',
  scheduled: 'text-blue-800',
  completed: 'text-neutral-500',
  cancelled: 'text-red-500',
};

const TIMELINE_LABELS = {
  status_change: 'Status changed',
  admin_update: 'Admin updated',
  application_submitted: 'Application submitted',
  admin_notification_sent: 'Admin new-application email sent',
  admin_notification_failed: 'Admin new-application email FAILED',
  submission_confirmation_sent: 'Applicant confirmation email sent',
  submission_confirmation_failed: 'Applicant confirmation email FAILED',
  accepted: 'Application accepted',
  declined: 'Application declined',
  payment_email_sent: 'Payment invitation sent',
  payment_email_resent: 'Payment invitation re-sent',
  booking_email_sent: 'Booking link sent',
  booking_email_resent: 'Booking link re-sent',
  payment_received: 'Payment received',
  cal_booking_created: 'Session booked on Cal.com',
  cal_booking_rescheduled: 'Session rescheduled on Cal.com',
  cal_booking_cancelled: 'Session cancelled on Cal.com',
  prep_reminder_sent: '24-hr prep reminder sent',
  agreement_accepted: 'Purchase agreement accepted',
  checkout_created: 'Stripe checkout session created',
  completed: 'Marked completed',
};

const dollars = (c) => (c == null ? '—' : `$${(c / 100).toFixed(2)}`);

export default function StrategyIntensiveAdmin() {
  const [data, setData] = useState({ items: [], total: 0, stats: {} });
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async (s = status, query = q) => {
    const params = {};
    if (s && s !== 'all') params.status = s;
    if (query?.trim()) params.q = query.trim();
    const r = await adminApi.get('/admin/strategy-intensive/applications', { params });
    setData(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(status, q); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(() => load(status, q), 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div data-testid="admin-strategy-intensive-view">
      <header className="mb-8">
        <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500 mb-2">Strategy Intensive</div>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl lg:text-4xl text-neutral-900">Applications</h1>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-6">
        {['new', 'accepted', 'awaiting_payment', 'awaiting_scheduling', 'scheduled', 'completed'].map((s) => (
          <div key={s} className="bg-white border border-neutral-200/60 rounded-xl px-4 py-3">
            <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">{STATUS_LABELS[s]}</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-2xl text-neutral-900 tabular-nums">{data.stats?.[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <input
          data-testid="si-search-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, business…"
          className="bg-white border border-neutral-200 rounded-full px-4 py-2 text-[13px] focus:outline-none focus:border-neutral-900 w-full lg:w-80"
        />
        <div className="flex gap-2 flex-wrap">
          {['all', 'new', 'under_review', 'accepted', 'awaiting_payment', 'awaiting_scheduling', 'scheduled', 'completed', 'declined'].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`si-filter-${s}`}
              className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full ${status === s ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}>
              {s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-neutral-200/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[720px]">
            <thead className="bg-neutral-50/70">
              <tr>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Applicant</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Business</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Status</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200/60">
              {data.items.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-neutral-500">No applications in this view yet.</td></tr>}
              {data.items.map((app) => (
                <tr key={app.id} onClick={() => setSelected(app.id)} data-testid={`si-row-${app.id}`} className="cursor-pointer hover:bg-neutral-50/70">
                  <td className="px-5 py-3"><div className="text-neutral-900">{app.name}</div><div className="text-neutral-500 text-[11px]">{app.email}</div></td>
                  <td className="px-5 py-3 text-neutral-700 truncate max-w-[220px]">{app.business_name || '—'}</td>
                  <td className="px-5 py-3"><span className={`uppercase tracking-[0.18em] text-[10px] ${STATUS_COLORS[app.status] || 'text-neutral-500'}`}>{STATUS_LABELS[app.status] || app.status}</span></td>
                  <td className="px-5 py-3 text-neutral-500 text-[11px]">{app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <ApplicationDetail id={selected} onClose={() => setSelected(null)} onChanged={() => load()} />}
    </div>
  );
}

function ApplicationDetail({ id, onClose, onChanged }) {
  const [app, setApp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');

  const load = async () => {
    const r = await adminApi.get(`/admin/strategy-intensive/applications/${id}`);
    setApp(r.data);
    setNotes(r.data.notes || '');
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const run = async (label, fn) => {
    setBusy(true);
    try { await fn(); toast.success(label); onChanged(); await load(); }
    catch (e) { if (e?.message === 'cancelled') return; toast.error(e?.response?.data?.detail || `${label} failed.`); }
    finally { setBusy(false); }
  };

  if (!app) return null;

  const price = dollars(app.cfg_price_cents);
  const isPaid = ['awaiting_scheduling', 'scheduled', 'completed'].includes(app.status);
  const canAcceptDecline = ['new', 'under_review'].includes(app.status);
  const canMarkUnderReview = app.status === 'new';
  const canResendPayment = ['accepted', 'awaiting_payment'].includes(app.status) && !!app.payment_token;
  const canSendBooking = ['awaiting_scheduling', 'scheduled'].includes(app.status);
  const bookingAlreadySent = !!app.booking_email_sent_at;
  const canMarkCompleted = app.status === 'scheduled';

  const accept = () => run('Approved. No email was sent. Use Request Payment when ready.', async () => {
    if (!window.confirm(`Approve ${app.name} for a Strategy Intensive? No email will be sent yet. You can request payment as a separate step.`)) throw new Error('cancelled');
    await adminApi.post(`/admin/strategy-intensive/applications/${id}/accept`);
  });
  const decline = () => run('Declined — email sent.', async () => {
    const reason = window.prompt('Optional decline reason (internal, not shown to applicant):') || '';
    await adminApi.post(`/admin/strategy-intensive/applications/${id}/decline`, { reason });
  });
  const resendPayment = () => run(app.payment_email_sent_at ? `Payment request re-sent to ${app.email}.` : `Payment request sent to ${app.email}.`, async () => {
    if (app.payment_email_sent_at && !window.confirm(`A payment request was already sent on ${new Date(app.last_payment_email_sent_at || app.payment_email_sent_at).toLocaleString()}. Resend it to ${app.email}?`)) throw new Error('cancelled');
    await adminApi.post(`/admin/strategy-intensive/applications/${id}/request-payment`, app.payment_email_sent_at ? { resend: true } : {});
  });
  const sendBooking = () => run(bookingAlreadySent ? 'Booking link re-sent.' : 'Booking link sent.', async () => {
    if (bookingAlreadySent && !window.confirm(`Resend booking-link email to ${app.email}?`)) throw new Error('cancelled');
    await adminApi.post(`/admin/strategy-intensive/applications/${id}/send-booking-email`, bookingAlreadySent ? { resend: true } : {});
  });
  const setStatus = (s) => run('Status updated.', () => adminApi.patch(`/admin/strategy-intensive/applications/${id}`, { status: s }));
  const markCompleted = () => run('Marked completed.', () => adminApi.patch(`/admin/strategy-intensive/applications/${id}`, { status: 'completed' }));
  const saveNotes = () => run('Notes saved.', () => adminApi.patch(`/admin/strategy-intensive/applications/${id}`, { notes }));

  const copy = async (url, label) => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); toast.success(`${label} copied.`); }
    catch { toast.error('Copy failed — please copy manually.'); }
  };

  // Next-step banner
  const banner = (() => {
    if (canAcceptDecline) return { text: 'Review the application, then Approve or Decline. Approving does not send an email.', tone: 'action' };
    if (['accepted', 'awaiting_payment'].includes(app.status) && !app.payment_email_sent_at) return { text: `Approved. Next step: Request Payment (${price}).`, tone: 'action' };
    if (['accepted', 'awaiting_payment'].includes(app.status)) return { text: `Payment requested ${new Date(app.last_payment_email_sent_at || app.payment_email_sent_at).toLocaleString()}${app.payment_email_sent_count > 1 ? ` (${app.payment_email_sent_count} sends)` : ''}. Waiting on ${price} payment.`, tone: 'wait' };
    if (app.status === 'awaiting_scheduling') return { text: bookingAlreadySent ? 'Payment received. Booking link sent — waiting on Cal.com booking. Use Resend Booking Link if needed.' : 'Payment received. Next step: Send Booking Link so the client can schedule.', tone: bookingAlreadySent ? 'wait' : 'action' };
    if (app.status === 'scheduled') return { text: `Session scheduled${app.scheduled_at ? ` for ${new Date(app.scheduled_at).toLocaleString()}` : ''}. Mark Completed after the session.`, tone: 'success' };
    if (app.status === 'completed') return { text: 'Engagement fully closed.', tone: 'success' };
    if (app.status === 'declined') return { text: 'Application declined — decline email was sent to the applicant.', tone: 'info' };
    return null;
  })();

  const agreement = app.purchase_agreement;

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-neutral-900/30 backdrop-blur-sm" />
      <aside data-testid="si-drawer" className="fixed right-0 top-0 z-50 h-full w-full lg:w-[760px] bg-[#FAF8F5] border-l border-neutral-200/70 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#FAF8F5]/95 backdrop-blur border-b border-neutral-200/60 px-6 lg:px-8 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-1">{STATUS_LABELS[app.status] || app.status}</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-2xl text-neutral-900 truncate">{app.name}</div>
            <div className="text-neutral-500 text-[12px] truncate">{app.email}{app.phone ? ' · ' + app.phone : ''}</div>
          </div>
          <button onClick={onClose} data-testid="si-drawer-close" className="text-neutral-500 hover:text-neutral-900 text-2xl px-2 shrink-0">×</button>
        </div>

        <div className="px-6 lg:px-8 py-6 space-y-6">
          {banner && (
            <div data-testid="si-next-step-banner"
              className={`px-4 py-3 border rounded-lg text-[13px] ${
                banner.tone === 'action' ? 'bg-[#C8A15A]/10 border-[#C8A15A]/40 text-[#8a6c30]' :
                banner.tone === 'wait' ? 'bg-blue-50 border-blue-200 text-blue-800' :
                banner.tone === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                'bg-neutral-100 border-neutral-200 text-neutral-700'}`}>
              <span className="uppercase tracking-[0.18em] text-[10px] mr-2">Next</span>{banner.text}
            </div>
          )}

          {/* Primary lifecycle actions */}
          <div className="flex flex-wrap gap-2">
            {canAcceptDecline && (
              <>
                <button onClick={accept} disabled={busy} data-testid="si-accept-btn"
                  className="bg-emerald-700 text-white uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-emerald-800 disabled:opacity-50">
                  Approve
                </button>
                <button onClick={decline} disabled={busy} data-testid="si-decline-btn"
                  className="border border-red-500 text-red-600 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-red-50 disabled:opacity-50">
                  Decline
                </button>
              </>
            )}
            {canMarkUnderReview && (
              <button onClick={() => setStatus('under_review')} disabled={busy} data-testid="si-under-review-btn"
                className="border border-neutral-300 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">
                Mark Under Review
              </button>
            )}
            {canResendPayment && (
              <button onClick={resendPayment} disabled={busy} data-testid="si-resend-payment-btn"
                className="border border-neutral-400 text-neutral-700 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100 disabled:opacity-50">
                {busy ? 'Sending…' : (app.payment_email_sent_at ? 'Resend Payment Request' : 'Request Payment')}
              </button>
            )}
            {canSendBooking && (
              <button onClick={sendBooking} disabled={busy} data-testid={bookingAlreadySent ? 'si-resend-booking-btn' : 'si-send-booking-btn'}
                className={`${bookingAlreadySent ? 'border border-neutral-400 text-neutral-700 hover:bg-neutral-100' : 'bg-neutral-900 text-white hover:bg-neutral-800'} uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full disabled:opacity-50`}>
                {busy ? 'Sending…' : (bookingAlreadySent ? 'Resend Booking Link' : 'Send Booking Link')}
              </button>
            )}
            {canMarkCompleted && (
              <button onClick={markCompleted} disabled={busy} data-testid="si-mark-completed-btn"
                className="border border-neutral-300 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">
                Mark Completed
              </button>
            )}
          </div>

          {/* Business snapshot */}
          <Section title="Business">
            <Row label="Business" value={app.business_name} />
            <Row label="Website" value={app.business_website} link />
            <Row label="Social" value={app.social_media} />
            <Row label="Length" value={app.business_length} />
            <Row label="Stage" value={app.business_stage} />
          </Section>

          {/* Submission email delivery — makes it visible whether the two auto-emails fired */}
          {app.submission_emails && (
            <Section title="Submission Emails">
              <SubmissionEmailStatus label="Applicant confirmation" recipient={app.email} sentAt={app.submission_emails.applicant_sent_at} messageId={app.submission_emails.applicant_message_id} error={app.submission_emails.applicant_error} testId="applicant" />
              <SubmissionEmailStatus label="Admin new-application" recipient="admin" sentAt={app.submission_emails.admin_sent_at} messageId={app.submission_emails.admin_message_id} error={app.submission_emails.admin_error} testId="admin-notif" />
            </Section>
          )}

          <Section title="Their Application">
            <Block label="Offer" value={app.offer_description} />
            <Block label="Audience" value={app.target_audience} />
            <Block label="Biggest Challenge *" value={app.biggest_challenge} />
            <Block label="Session Goal" value={app.session_goal} />
            <Block label="Tried Before" value={app.tried_before} />
            <Row label="Ready to Implement" value={app.ready_to_implement} />
            <Block label="Additional Notes" value={app.additional_notes} />
          </Section>

          {/* Payment section */}
          {app.payment_url && (
            <Section title="Payment">
              <div data-testid="si-payment-request-status" className={`mb-3 p-3 border rounded-lg text-[12px] ${app.payment_email_sent_at ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-neutral-100 border-neutral-200 text-neutral-700'}`}>
                {app.payment_email_sent_at
                  ? <>Payment Requested ✓ · last sent {new Date(app.last_payment_email_sent_at || app.payment_email_sent_at).toLocaleString()} · {app.payment_email_sent_count || 1} send{(app.payment_email_sent_count || 1) > 1 ? 's' : ''}{isPaid ? '' : ' · not yet paid'}</>
                  : 'Payment not requested yet.'}
              </div>
              <LinkCard
                label="Payment Link"
                url={app.payment_url}
                sentAt={app.payment_email_sent_at}
                sentCount={app.payment_email_sent_count}
                lastSentAt={app.last_payment_email_sent_at}
                onCopy={() => copy(app.payment_url, 'Payment link')}
                testId="si-payment-link"
              />
              {app.paid_at && (
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-[12px] text-emerald-900">
                  ✓ Paid {new Date(app.paid_at).toLocaleString()} · {dollars(app.amount_paid_cents)}
                  {app.stripe_checkout_session_id && <div className="text-[10px] text-emerald-700 mt-1 font-mono truncate">Stripe: {app.stripe_checkout_session_id}</div>}
                </div>
              )}
              {agreement && (
                <details className="mt-3">
                  <summary className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 cursor-pointer">View acknowledgment evidence</summary>
                  <div className="mt-3 p-3 bg-white border border-neutral-200 rounded-lg text-[11px] text-neutral-700">
                    <div>Accepted: <strong>{agreement.accepted ? 'Yes' : 'No'}</strong> · {agreement.accepted_at ? new Date(agreement.accepted_at).toLocaleString() : '—'}</div>
                    <div>IP: <span className="font-mono">{agreement.client_ip || '—'}</span></div>
                    <div className="truncate">UA: <span className="font-mono">{agreement.user_agent || '—'}</span></div>
                    <div className="mt-1">Policy Versions:</div>
                    <ul className="list-disc pl-5">{Object.entries(agreement.policy_versions_accepted || {}).map(([k, v]) => <li key={k}>{k}: <strong>{v}</strong></li>)}</ul>
                    <p className="mt-2 text-[11px] text-neutral-700 italic leading-relaxed">{agreement.acknowledgment_text}</p>
                  </div>
                </details>
              )}
            </Section>
          )}

          {/* Booking / Scheduling */}
          {isPaid && (
            <Section title="Scheduling">
              {app.schedule_url && (
                <LinkCard
                  label="Booking Link"
                  url={app.schedule_url}
                  sentAt={app.booking_email_sent_at}
                  sentCount={app.booking_email_sent_count}
                  lastSentAt={app.last_booking_email_sent_at}
                  onCopy={() => copy(app.schedule_url, 'Booking link')}
                  testId="si-booking-link"
                />
              )}
              {app.cal_com_event_url && (
                <div className="mt-2 text-[11px] text-neutral-500">Cal.com event: <a href={app.cal_com_event_url} target="_blank" rel="noreferrer" className="underline">{app.cal_com_event_url}</a></div>
              )}
              {app.scheduled_at && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-[12px] text-blue-900">
                  <div className="uppercase tracking-[0.18em] text-[9px] text-blue-700 mb-1">Scheduled</div>
                  <div><strong>{new Date(app.scheduled_at).toLocaleString()}</strong>{app.session_timezone ? ` (${app.session_timezone})` : ''}</div>
                  {app.cal_meeting_url && <div className="mt-1"><a href={app.cal_meeting_url} target="_blank" rel="noreferrer" className="underline text-blue-800">Meeting link ↗</a></div>}
                  {app.cal_booking_uid && <div className="text-[10px] text-blue-700 mt-1 font-mono truncate">Cal.com uid: {app.cal_booking_uid}</div>}
                </div>
              )}
              {app.prep_url && (
                <div className="mt-3 p-3 bg-white border border-neutral-200 rounded-lg">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">Prep Questionnaire · {(app.questionnaire_status || 'not_started').replace(/_/g, ' ')}</div>
                      <div className="text-[11px] text-neutral-600 break-all">{app.prep_url}</div>
                    </div>
                    <button onClick={() => copy(app.prep_url, 'Prep link')} data-testid="si-copy-prep" className="border border-neutral-300 uppercase tracking-[0.18em] text-[10px] px-3 py-1.5 rounded-full hover:bg-neutral-100">Copy</button>
                  </div>
                  {app.reminder_sent_at && <div className="text-[10px] text-neutral-500 mt-1">24-hr reminder sent {new Date(app.reminder_sent_at).toLocaleString()}</div>}
                </div>
              )}
              {app.questionnaire_status === 'submitted' && (app.questionnaire_responses || []).length > 0 && (
                <details className="mt-3">
                  <summary className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 cursor-pointer">View {app.questionnaire_responses.length} prep responses</summary>
                  <div className="mt-3 space-y-3">
                    {app.questionnaire_responses.map((r) => (
                      <div key={r.question_id} className="p-3 bg-white border border-neutral-200 rounded-lg">
                        <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-[14px] text-neutral-900 mb-1">{r.question}</div>
                        <div className="text-[13px] text-neutral-700 whitespace-pre-wrap">{r.answer || <em className="text-neutral-400">No answer</em>}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </Section>
          )}

          {/* Activity Timeline */}
          <Section title="Activity Timeline">
            <div data-testid="si-activity-timeline" className="relative pl-6 border-l border-neutral-200 space-y-3">
              {(app.activity_log || []).length === 0 && <div className="text-[12px] text-neutral-500 italic">No activity yet.</div>}
              {(app.activity_log || []).slice().reverse().map((a, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[27px] top-1 w-2.5 h-2.5 rounded-full bg-[#C8A15A] border-2 border-[#FAF8F5]" />
                  <div className="text-[13px] text-neutral-900">{TIMELINE_LABELS[a.action] || a.action.replace(/_/g, ' ')}</div>
                  {a.detail && <div className="text-[11px] text-neutral-500 mt-0.5 break-words">{a.detail}</div>}
                  <div className="text-[10px] text-neutral-400 mt-0.5 uppercase tracking-[0.14em]">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* Private Notes */}
          <Section title="Private Notes">
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="si-notes-input" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" />
            <button onClick={saveNotes} disabled={busy} data-testid="si-save-notes-btn" className="mt-3 border border-neutral-300 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">Save Notes</button>
          </Section>
        </div>
      </aside>
    </>
  );
}

function SubmissionEmailStatus({ label, recipient, sentAt, messageId, error, testId }) {
  const ok = !!sentAt;
  return (
    <div className={`p-3 border rounded-lg ${ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
         data-testid={`si-submission-email-${testId}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">{label}{recipient && recipient !== 'admin' ? ` · ${recipient}` : ''}</div>
          <div className={`text-[13px] ${ok ? 'text-emerald-900' : 'text-red-800'}`}>
            {ok ? `✓ Sent ${new Date(sentAt).toLocaleString()}` : `✗ Failed — ${error || 'unknown error'}`}
          </div>
        </div>
      </div>
      {messageId && <div className="text-[10px] text-neutral-500 mt-1 font-mono truncate">Resend id: {messageId}</div>}
    </div>
  );
}

function LinkCard({ label, url, sentAt, sentCount, lastSentAt, onCopy, testId }) {
  return (
    <div className="p-3 bg-white border border-neutral-200 rounded-lg" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">{label}</div>
          <div className="text-[12px] text-neutral-800 break-all">{url}</div>
        </div>
        <div className="flex gap-2">
          <a href={url} target="_blank" rel="noreferrer" data-testid={`${testId}-open`} className="border border-neutral-300 uppercase tracking-[0.18em] text-[10px] px-3 py-1.5 rounded-full hover:bg-neutral-100">Open</a>
          <button onClick={onCopy} data-testid={`${testId}-copy`} className="border border-neutral-300 uppercase tracking-[0.18em] text-[10px] px-3 py-1.5 rounded-full hover:bg-neutral-100">Copy</button>
        </div>
      </div>
      {sentAt && (
        <div className="text-[11px] text-neutral-600 mt-2" data-testid={`${testId}-sent-status`}>
          ✓ Sent {new Date(sentAt).toLocaleString()}
          {sentCount > 1 && ` · ${sentCount} times`}
          {lastSentAt && sentCount > 1 && ` · last on ${new Date(lastSentAt).toLocaleString()}`}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, value, mono, link }) {
  const display = value || '—';
  return (
    <div className="flex items-baseline gap-3 text-[13px]">
      <div className="w-28 shrink-0 text-neutral-500 text-[11px] uppercase tracking-[0.18em]">{label}</div>
      <div className={`text-neutral-800 break-words min-w-0 ${mono ? 'font-mono text-[11px]' : ''}`}>
        {link && value ? <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-neutral-800 underline decoration-neutral-300 hover:decoration-neutral-800">{value}</a> : display}
      </div>
    </div>
  );
}
function Block({ label, value }) {
  return (
    <div>
      <div className="text-neutral-500 text-[11px] uppercase tracking-[0.18em] mb-1">{label}</div>
      <div className="text-[13px] text-neutral-800 whitespace-pre-wrap">{value || <em className="text-neutral-400">—</em>}</div>
    </div>
  );
}
